import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements, PaymentElement, useElements, useStripe,
} from '@stripe/react-stripe-js';
import { useTranslation } from '../LanguageContext';

/**
 * The card form, once. Every payment in the product mounts this one.
 *
 * Subscriptions, stars and coffees used to redirect to a hosted Stripe Checkout
 * page; the wallet top-up was the only thing that collected a card in place.
 * Now that they all do, the Elements plumbing — the cached Stripe.js loader, the
 * appearance, `redirect: 'if_required'`, reading `paymentIntent.status` rather
 * than trusting the absence of an error — lives here rather than in four copies
 * that would drift apart.
 *
 * Handles both kinds of intent, because "collect a card to charge now" and
 * "collect a card to keep" are the same form with a different verb:
 *   mode="payment" -> confirmPayment (a purchase or a first invoice)
 *   mode="setup"   -> confirmSetup   (replacing the card behind a subscription)
 */

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

// Stripe.js is fetched once per publishable key. Remounting a sheet must not
// re-download it, and every sheet in the app shares this cache.
const stripeCache = {};
export const getStripe = (key) => {
    if (!key) return null;
    if (!stripeCache[key]) stripeCache[key] = loadStripe(key);
    return stripeCache[key];
};

const APPEARANCE = {
    theme: 'stripe',
    variables: {
        colorPrimary: '#20c9b5',
        borderRadius: '12px',
        fontFamily: 'inherit',
    },
};

function IntentForm({
    mode, amountUsd, amountLabel, submitLabel, returnPath,
    onSucceeded, onCancel, cancelLabel, note, children,
}) {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const submit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements || busy) return;

        setBusy(true);
        setError('');

        const confirmParams = {
            return_url: `${window.location.origin}${returnPath || window.location.pathname}`,
        };

        // `redirect: 'if_required'` keeps a card payment on this page and only
        // leaves for methods that genuinely cannot be completed inline.
        const result = mode === 'setup'
            ? await stripe.confirmSetup({ elements, confirmParams, redirect: 'if_required' })
            : await stripe.confirmPayment({ elements, confirmParams, redirect: 'if_required' });

        if (result.error) {
            setError(result.error.message || t('billing.paymentFailed'));
            setBusy(false);
            return;
        }

        const intent = mode === 'setup' ? result.setupIntent : result.paymentIntent;
        if (intent?.status === 'succeeded') {
            // Deliberately stays busy: the caller settles with the backend next,
            // and a re-enabled button in the meantime invites a second payment.
            const failure = await onSucceeded(intent);
            if (failure) {
                setError(failure);
                setBusy(false);
            }
            return;
        }

        // `processing` is a real outcome for some methods — the money is on its
        // way and saying "it failed" would be a lie.
        setError(intent?.status === 'processing'
            ? t('billing.paymentProcessing')
            : t('billing.paymentIncomplete'));
        setBusy(false);
    };

    return (
        <form onSubmit={submit} className="space-y-4">
            {amountUsd != null && (
                <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">
                        {amountLabel || t('billing.amountToPay')}
                    </span>
                    <span className="text-lg font-black text-slate-900 tabular-nums">
                        {usd(amountUsd)}
                    </span>
                </div>
            )}

            {children}

            <PaymentElement options={{ layout: 'tabs' }} />

            {error && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                    {error}
                </p>
            )}

            <div className="flex gap-2">
                {onCancel && (
                    <button type="button" onClick={onCancel} disabled={busy}
                        className="btn-secondary flex-1 py-2.5 text-sm">
                        {cancelLabel || t('billing.cancel')}
                    </button>
                )}
                <button type="submit" disabled={!stripe || busy}
                    className="btn-primary flex-1 py-2.5 text-sm font-bold flex items-center justify-center gap-2">
                    {busy && <span className="spinner-sm" />}
                    {submitLabel || t('billing.payNow')}
                </button>
            </div>

            <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                {note || t('billing.securityNote')}
            </p>
        </form>
    );
}

/**
 * The card step. Mounts only once a client secret exists.
 *
 * `onSucceeded(intent)` runs after Stripe confirms and before the form releases
 * its button — return a string from it to show that string as an error, or
 * nothing to leave the form spinning while the caller moves on.
 */
function StripeIntentForm({
    clientSecret, publishableKey, mode = 'payment', ...rest
}) {
    const { t } = useTranslation();
    const stripePromise = useMemo(() => getStripe(publishableKey), [publishableKey]);

    if (!clientSecret || !stripePromise) {
        // Without a pk_ key the Payment Element cannot mount at all — name the
        // cause rather than rendering an empty box.
        return (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                {t('billing.noPublishableKey')}
            </p>
        );
    }

    return (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: APPEARANCE }}>
            <IntentForm mode={mode} {...rest} />
        </Elements>
    );
}

/** A centred sheet to hang a card form in, for callers with no card of their own. */
function StripeSheet({ title, subtitle, onClose, closable = true, children, wide }) {
    useEffect(() => {
        const onKey = (event) => { if (event.key === 'Escape' && closable) onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [closable, onClose]);

    return (
        <div className="fixed inset-0 z-[9600] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className={`bg-white w-full ${wide ? 'sm:max-w-lg' : 'sm:max-w-md'} rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] flex flex-col overflow-hidden`}>
                <div className="flex items-start justify-between px-6 pt-6 pb-3 flex-shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-lg font-black text-slate-900">{title}</h3>
                        {subtitle && (
                            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{subtitle}</p>
                        )}
                    </div>
                    {closable && (
                        <button onClick={onClose}
                            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 transition flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                <div className="px-6 pb-6 overflow-y-auto custom-scrollbar">{children}</div>
            </div>
        </div>
    );
}

/** The green tick every one of these flows ends on. */
function PaymentDone({ title, detail, warning, onClose, closeLabel }) {
    const { t } = useTranslation();
    return (
        <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
            </div>
            <p className="text-base font-black text-slate-900">{title}</p>
            {warning ? (
                <p className="text-xs text-amber-600 mt-1 leading-relaxed">{warning}</p>
            ) : detail ? (
                <p className="text-sm text-slate-500 mt-1">{detail}</p>
            ) : null}
            {onClose && (
                <button onClick={onClose} className="btn-primary w-full py-2.5 text-sm mt-5">
                    {closeLabel || t('billing.close')}
                </button>
            )}
        </div>
    );
}

export { StripeIntentForm, StripeSheet, PaymentDone, usd };
